import { z } from 'zod';
import { ClientLifecycleSchema } from '../clients.js';
import {
  ENGAGEMENT_SCORE_MAX,
  ENGAGEMENT_SCORE_MIN,
  EngagementPhaseSchema,
  EngagementScoreSchema,
  EngagementStageSchema,
} from './engagement.js';

/// Free-text list search (`q`) cap for the Engagement read surface — bounds the
/// ILIKE pattern the backend builds so a hostile caller cannot send an
/// unbounded term (mirror of `LEAD_LIST_SEARCH_MAX`).
export const ENGAGEMENT_LIST_SEARCH_MAX = 200;

/**
 * @aiPurpose the manager-facing v4 funnel BUCKETS the `GET /engagements` list
 *   filters + counts by (#2257 FR-1 / #2247 FR-1). A wire-only query concept —
 *   each bucket maps to a `(phase, stage)` predicate, an `assigneeId`/`clientId`
 *   null narrow, or the terminal-stage set. `NO_CLIENT` is a saved-list FILTER,
 *   NOT a stage/column (P-4). `ALL` applies no funnel narrow (still
 *   visibility-gated).
 * @aiInvariant not a Prisma enum — the DB stores `phase`/`stage`, and the
 *   backend maps each bucket onto those columns in one place
 *   (`engagement-list-query.ts`). UI/i18n say «Все/Новые/…» — never the enum
 *   token literally (D-A).
 */
export enum EngagementBucket {
  ALL = 'ALL',
  UNASSIGNED = 'UNASSIGNED',
  NEW = 'NEW',
  IN_WORK = 'IN_WORK',
  CLOSING = 'CLOSING',
  DEAL = 'DEAL',
  POSTPONED = 'POSTPONED',
  NO_CLIENT = 'NO_CLIENT',
  CLOSED = 'CLOSED',
}
export const EngagementBucketSchema = z.nativeEnum(EngagementBucket);

/**
 * @aiPurpose cursor-stable sort order for the list. Both orders key on
 *   `(updatedAt, id)` — the same pair the opaque cursor encodes — so pagination
 *   stays deterministic under either direction. `recent` (default) is
 *   newest-touched first; `oldest` reverses it.
 */
export const EngagementListSortSchema = z.enum(['recent', 'oldest']);
export type EngagementListSort = z.infer<typeof EngagementListSortSchema>;

/**
 * @aiPurpose query string for `GET /engagements` (#2257 FR-1). Cursor-paginated
 *   list over the read-only Engagement projection with a bucket selector, a
 *   qualification-`score` filter, a bound-`client` filter, free-text `search`,
 *   and a `phase` narrow (#2248 reads the deals view via `phase=DEAL`, FR-4).
 * @aiInvariant `.strict()` blocks unknown keys. `bucket` defaults to `ALL` and
 *   `sort` to `recent`. `limit` clamps to 1..100 (default 50). `cursor` is the
 *   opaque base64url(`{updatedAt, id}`) the list returns. `q` is trimmed +
 *   length-capped; the backend matches it case-insensitively via parameterised
 *   ILIKE (never interpolated). `score`/`clientId`/`phase` narrow WITHIN the
 *   caller's visible scope — none of them widens visibility on its own.
 */
export const GetEngagementsQuerySchema = z
  .object({
    bucket: EngagementBucketSchema.default(EngagementBucket.ALL),
    phase: EngagementPhaseSchema.optional(),
    // Query-string arrives as a string → coerce, then apply the same 1..5 bound
    //   as `EngagementScoreSchema` (which is number-typed for JSON bodies).
    score: z.coerce.number().int().min(ENGAGEMENT_SCORE_MIN).max(ENGAGEMENT_SCORE_MAX).optional(),
    clientId: z.string().uuid().optional(),
    q: z.string().trim().min(1).max(ENGAGEMENT_LIST_SEARCH_MAX).optional(),
    sort: EngagementListSortSchema.default('recent'),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type GetEngagementsQuery = z.infer<typeof GetEngagementsQuerySchema>;

/**
 * @aiPurpose one row of the `GET /engagements` list (#2257 FR-1). The funnel
 *   projection fields live on the `Engagement`; the display `name`/`company` are
 *   JOINed from the source `Lead` (+ its bound `Client`) because `Lead` is the
 *   PII system-of-record during coexistence — the caller already passed the
 *   row-visibility check to reach this shape.
 * @aiPii `name` (contact) + `company` (bound client name) are display PII,
 *   rendered only inside the manager workspace list. The quarantined
 *   `Engagement.intake` blob is NEVER projected here.
 * @aiInvariant `leadId` is the source Lead UUID the web routes `/leads/:id`
 *   through (BR-4); `name`/`company`/`leadId` are `null` only for an orphaned
 *   projection (source Lead hard-deleted → `leadId` SetNull). `assigneeId` is
 *   `null` for an Unassigned-pool projection (`Lead.managerId = null`, #2256).
 */
export const EngagementListRowSchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid().nullable(),
    leadCode: z.string(),
    dealCode: z.string().nullable(),
    phase: EngagementPhaseSchema,
    stage: EngagementStageSchema,
    score: EngagementScoreSchema.nullable(),
    clientId: z.string().uuid().nullable(),
    resumeAt: z.string().datetime().nullable(),
    assigneeId: z.string().uuid().nullable(),
    // CRM v4 scope UI (#2638): JOINed assignee display name, emitted only when
    //   the caller opts in via `X-Magimp-Crm-V4-Scope-Ui`; null for the pool.
    //   OPTIONAL so default and fidelity-only responses stay byte-identical.
    assigneeName: z.string().nullable().optional(),
    name: z.string().nullable(),
    company: z.string().nullable(),
    // CRM v4 fidelity (#2303): the source `Lead.title` («тема лида»), JOINed like
    //   `name`. Emitted ONLY when the caller opts in via the
    //   `X-Magimp-Crm-V4-Fidelity` header (D-6); OPTIONAL so default responses
    //   stay valid under `.strict()` for stale bundles, `null` when set-but-empty.
    title: z.string().nullable().optional(),
    // CRM v4 fidelity (#2304): the lead's source display name
    //   (`LeadSource.displayName`, JOINed via `Lead.sourceId`; `null` when the
    //   lead has no source) plus the engagement's single OPEN `EngagementTask`
    //   presence + its `dueAt`. Overdue is derived CLIENT-side from
    //   `openTaskDueAt` — never precomputed here. Same opt-in header gate as
    //   `title` (D-6); OPTIONAL so default responses stay byte-identical.
    source: z.string().nullable().optional(),
    hasOpenTask: z.boolean().optional(),
    openTaskDueAt: z.string().datetime().nullable().optional(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type EngagementListRow = z.infer<typeof EngagementListRowSchema>;

/**
 * @aiPurpose server-computed per-bucket counts for the filter chips (#2257
 *   FR-1). Each count reflects the caller's visible set with the non-bucket
 *   filters (score/client/search) applied but NOT the bucket/phase narrow, so
 *   switching buckets never zeroes the other chips. The Unassigned-pool count
 *   is optional because it is emitted only for scope-UI callers (#2638).
 */
export const EngagementBucketCountsSchema = z
  .object({
    all: z.number().int().min(0),
    new: z.number().int().min(0),
    inWork: z.number().int().min(0),
    closing: z.number().int().min(0),
    deal: z.number().int().min(0),
    postponed: z.number().int().min(0),
    noClient: z.number().int().min(0),
    closed: z.number().int().min(0),
    // CRM v4 scope UI (#2638): opt-in Unassigned-pool count. Optional keeps
    //   default and fidelity-only response envelopes byte-identical.
    unassigned: z.number().int().min(0).optional(),
  })
  .strict();
export type EngagementBucketCounts = z.infer<typeof EngagementBucketCountsSchema>;

/**
 * @aiPurpose response envelope for `GET /engagements` (#2257 FR-1).
 * @aiInvariant `nextCursor` is `null` exactly when the query returned ≤ `limit`
 *   rows; otherwise it encodes the `(updatedAt, id)` of the last returned row.
 *   `counts` always carries every legacy bucket key; scope-UI callers also
 *   receive the optional `unassigned` key (#2638).
 */
export const EngagementListViewSchema = z
  .object({
    items: z.array(EngagementListRowSchema),
    nextCursor: z.string().min(1).nullable(),
    counts: EngagementBucketCountsSchema,
  })
  .strict();
export type EngagementListView = z.infer<typeof EngagementListViewSchema>;

/**
 * @aiPurpose compact bound-client summary embedded in the engagement card
 *   (#2257 FR-2). Carries only the header fields the card needs — id + business
 *   name + derived lifecycle — never contacts / legal entities / requisites.
 */
export const EngagementClientSummarySchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    lifecycle: ClientLifecycleSchema,
  })
  .strict();
export type EngagementClientSummary = z.infer<typeof EngagementClientSummarySchema>;

/**
 * @aiPurpose the engagement card projection returned by `GET
 *   /engagements/by-lead/:leadId` (#2257 FR-2). Resolves a **Lead UUID** via
 *   `Engagement.leadId` so #2247's `/leads/:id` route needs no id remap (BR-4).
 *   A superset of `EngagementSummary` plus the Lead-sourced display
 *   `name`/`company` and the bound-`client` summary the card header renders.
 * @aiPii `name`/`company` are the only display PII; the quarantined
 *   `Engagement.intake` blob is NEVER projected (BR-2 / T-3).
 * @aiInvariant `name`/`company`/`leadId` follow the same null rules as the list
 *   row; `client` is `null` exactly when `clientId === null`.
 */
export const EngagementDetailViewSchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid().nullable(),
    leadCode: z.string(),
    dealCode: z.string().nullable(),
    phase: EngagementPhaseSchema,
    stage: EngagementStageSchema,
    score: EngagementScoreSchema.nullable(),
    clientId: z.string().uuid().nullable(),
    resumeAt: z.string().datetime().nullable(),
    assigneeId: z.string().uuid().nullable(),
    name: z.string().nullable(),
    company: z.string().nullable(),
    // CRM v4 fidelity (#2303): same opt-in `Lead.title` field as the list row —
    //   emitted only behind the `X-Magimp-Crm-V4-Fidelity` header (D-6).
    title: z.string().nullable().optional(),
    // CRM v4 fidelity (#2304): the lead's source display name, the lead's PII
    //   contacts (`phone`/`email` — DETAIL-ONLY, gated by the same `leads.read`
    //   guard that already fronts the card's intake-history PII), and the
    //   `assigneeId` user's display name (`null` when unassigned). Same opt-in
    //   header gate as `title` (D-6); OPTIONAL so default responses stay
    //   byte-identical for stale bundles.
    source: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    assigneeName: z.string().nullable().optional(),
    client: EngagementClientSummarySchema.nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type EngagementDetailView = z.infer<typeof EngagementDetailViewSchema>;
